import React, { memo, useEffect } from "react";
import styles from "./DashboardInput.module.css";
import {
  Autocomplete,
  Box,
  Chip,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import {
  CitySelector,
  CountrySelector,
  StateSelector,
} from "volkeno-react-country-state-city";
import "volkeno-react-country-state-city/dist/index.css";

const DSelectInput = ({
  label,
  required = false,
  customInputWrapperClass,
  customClassName,
  defaultDropdown,
  noInit,
  countryDropDown,
  stateDropDown,
  cityDropDown,
  placeHolder,
  regionOnchange,
  value,
  existingVendorList1,
  isEdit,
  countryid,
  stateid,
  onChange,
  error,
  options,
  disabled,
  multiselect,
  size,
  errorMsg,
  customClass,
  containerStyle,
  setIDValue,
  getIDValue,
  id,
  filterMatchFrom = "start",
  filterLimit,
}) => {
  useEffect(() => {
    if (!setIDValue) return;

    // Multi-select: keep ID(s) in sync with the selected display values.
    if (Array.isArray(value)) {
      const selectedTokens = value
        .map(
          (selectedValue) =>
            options?.tokens?.[options?.visibleOption?.indexOf(selectedValue)]
        )
        .filter((t) => t !== undefined);
      setIDValue(selectedTokens);
      return;
    }

    // Single-select: do not overwrite the token set by `getTokenSingleSelect` on change.
    // Also keep token in sync when parent sets `value` programmatically.
    if (typeof value === "string" && value.trim() !== "") {
      const idx = options?.visibleOption?.indexOf(value);
      const token = idx >= 0 ? options?.tokens?.[idx] : undefined;
      if (token !== undefined) setIDValue(token);
    }
  }, [options, value, setIDValue]);

  const getID = (event) => {
    const selectedValues = event;
    const selectedTokens =
      selectedValues && selectedValues?.length === 1
        ? [options?.tokens[options?.visibleOption?.indexOf(selectedValues[0])]]
        : selectedValues?.map(
            (selectedValue) =>
              options?.tokens[options?.visibleOption?.indexOf(selectedValue)]
          );
    setIDValue(selectedTokens);
  };

  const getTokenSingleSelect = (event) => {
    const selectedTokens =
      options?.tokens[options?.visibleOption?.indexOf(event)];
    setIDValue && setIDValue(selectedTokens);
  };

  const theme = createTheme({
    typography: {
      // fontSize: "1.4rem"
      htmlFontSize: 9.8,
    },
  });

  const normalizeText = (value) =>
    String(value ?? "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const getOptionText = (option, state) => {
    if (option === undefined || option === null) return "";
    if (typeof option === "string") return option;
    return (
      option?.label ??
      option?.name ??
      String(option ?? "")
    );
  };

  const resolvedOptions = getIDValue
    ? Array.isArray(options?.visibleOption)
      ? options.visibleOption
      : []
    : Array.isArray(options)
    ? options
    : Array.isArray(options?.visibleOption)
    ? options.visibleOption
    : [];

  const resolvedOptionsLength = resolvedOptions.length;
  const resolvedFilterLimit =
    typeof filterLimit === "number" ? filterLimit : resolvedOptionsLength > 2000 ? 200 : undefined;

  const filterOptions = (opts, state) => {
    if (!Array.isArray(opts)) return [];

    const needle = normalizeText(state?.inputValue);
    const seen = new Set();
    const filtered = [];

    if (!needle) {
      for (const option of opts) {
        const optionText = getOptionText(option, state);
        const normalizedKey = normalizeText(optionText);
        if (seen.has(normalizedKey)) continue;
        seen.add(normalizedKey);
        filtered.push(option);
        if (typeof resolvedFilterLimit === "number" && filtered.length >= resolvedFilterLimit) break;
      }
      return filtered;
    }

    for (const option of opts) {
      const haystack = normalizeText(getOptionText(option, state));

      const isMatch =
        filterMatchFrom === "start"
          ? haystack.startsWith(needle)
          : haystack.includes(needle);

      if (!isMatch) continue;

      if (seen.has(haystack)) continue;
      seen.add(haystack);
      filtered.push(option);
      if (typeof resolvedFilterLimit === "number" && filtered.length >= resolvedFilterLimit) break;
    }

    return filtered;
  };

  return (
    <ThemeProvider theme={theme}>
      {/* ${disabled && styles.diabledField} */}
      <div
        className={`${styles.select_container} ${
          customClass || customClassName || ""
        } `}
        style={{ width: `${size}`, ...(containerStyle || {}) }}
      >
        <label>
          {label}
          {required && <span className={styles.requiredMark}>*</span>}
        </label>
        {multiselect ? (
          <Autocomplete
            multiple
            options={options?.visibleOption || []}
            filterSelectedOptions
            // getOptionLabel={(option) => option.title}
            // defaultValue={[top100Films[13]]}
            // filterSelectedOptions
            autoHighlight={true}
            autoComplete={true}
            onChange={(event, newValue) => {
              const requested = Array.isArray(newValue) ? newValue : [];
              const nextValue = isEdit
                ? [
                    ...existingVendorList1,
                    ...requested.filter(
                      (option) => existingVendorList1.indexOf(option) === -1
                    ),
                  ]
                : requested;

              onChange(nextValue);
              getID(nextValue);
            }}
            sx={{
              minHeight: "4.2rem",
              height: "auto",
              fontSize: "1.4rem",
              transition: "0",
              ".Mui-expanded": {
                transition: "none",
              },
              ".MuiOutlinedInput-notchedOutline": {
                border: "0",
              },
              ".MuiAutocomplete-input": {
                fontSize: "1.4rem",
                fontFamily: "GilroyMedium",
                height: "2.6rem",
              },
              ".MuiFormLabel-root": {
                display: "none",
              },
              ".MuiChip-root": {
                height: "30px",
                padding: "5px",
              },
            }}
            size="small"
            placeholder={label}
            value={value ||[]}
            className={`${styles.select_wrapper_multiSelect}  ${
              error && styles.error
            }`}
            disabled={disabled}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  disabled={
                    isEdit ? existingVendorList1?.indexOf(option) !== -1 : false
                  }
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={!placeHolder ? label && label : placeHolder}
              />
            )}
            disableClearable={isEdit}
          />
        ) : (
          <Autocomplete
            options={resolvedOptions}
            sx={{
              height: "4.2rem",
              fontSize: "1.4rem",
              transition: "0",
              ".Mui-expanded": {
                transition: "none",
              },
              ".MuiOutlinedInput-notchedOutline": {
                border: "0",
              },
              ".MuiAutocomplete-input": {
                fontSize: "1.4rem",
                fontFamily: "GilroyMedium",
                height: "2.6rem",
              },
            }}
            componentsProps={{
              popper: {
                sx: {
                  fontFamily: "gilroyMedium",
                },
                modifiers: [
                  {
                    name: "flip",
                    enabled: false,
                  },
                  {
                    name: "preventOverflow",
                    enabled: true,
                  },
                ],
              },
            }}
            size="small"
            className={`${styles.select_wrapper} ${customInputWrapperClass}  ${
              error ? styles.error : ""
            }`}
            id={id ? id : ""}
            disabled={disabled}
            value={value}
            getOptionLabel={(option) => getOptionText(option)}
            onChange={(event, newValue) => {
              onChange(newValue, event);
              getIDValue && getTokenSingleSelect(newValue);
            }}
            filterOptions={filterOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                error={error}
                placeholder={!placeHolder ? label && label : placeHolder}
              />
            )}
          />
        )}

        {error && <p className={styles.error_message}>{errorMsg}</p>}
      </div>
    </ThemeProvider>
  );
};

export default memo(DSelectInput);
